import { useState } from "react";

// Get the Supabase anon key
const supabaseKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtza2hvdWh6bmp2bWVqaXh6bGJ2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzMwMTA0NzcsImV4cCI6MjA0ODU4NjQ3N30.8EcZRNvLg-4VfKD4kCs960TCqcvYI856dWfHfdiUj1M";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

interface SendTestEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateName: string;
  templateHtml: string;
  templateText?: string;
}

export function SendTestEmailModal({
  isOpen,
  onClose,
  templateName,
  templateHtml,

  templateText, // Not used in mock implementation but would be used in real implementation
}: SendTestEmailModalProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  // Using the shared Supabase client from @/integrations/supabase/client

  const handleSendTestEmail = async () => {
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter a recipient email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Use direct fetch to call the dedicated test email function
      const response = await fetch(
        "https://kskhouhznjvmejixzlbv.supabase.co/functions/v1/send-test-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Use the Supabase anon key
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            templateName,
            templateHtml,
            templateText,
            recipientEmail: email,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send test email");
      }

      toast({
        title: "Test Email Sent",
        description: `Test email has been sent to ${email}`,
      });

      onClose();
    } catch (error) {
      console.error("Error sending test email:", error);
      toast({
        title: "Error",
        description: "Failed to send test email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Test Email</DialogTitle>
          <DialogDescription>
            Send a test email to verify the template appearance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Recipient Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>Template: {templateName}</p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> This will send a real test email using AWS
              SES to the address you provide.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSendTestEmail} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Test Email"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
